import React, {FC} from 'react';
import {connect} from 'react-redux';
import FacebookLogin, {
    ReactFacebookFailureResponse,
    ReactFacebookLoginInfo,
} from 'react-facebook-login';
import {GoogleLogin, GoogleLoginResponse, GoogleLoginResponseOffline} from 'react-google-login';
import {propOr} from 'ramda';
import {LOGIN, sendHashedEmailEvent} from '@optimaxdev/analytics/mobile';

import {ApplicationStoreType, ActionCreatorType} from 'constants/flow/flowTypes';
import {getSocialToken, setError as setSocialsError} from 'reducers/oAuth/oAuth';
import {StoreInfoType} from 'reducers/storeInfo/storeInfoType';
import {Icon} from 'components/baseIcon/baseIcon';
import * as GoogleIcon from 'components/__helpers/icon/svg/common/google.svg';
import * as FacebookIcon from 'components/__helpers/icon/svg/common/facebook.svg';
import {sendAnalytics} from 'features/wizardMs/utils/sendAnalytics';
import {getStoreInfoState} from 'selectors/storeInfo/storeInfo';
import {LOGIN_METHOD} from 'constants/login';
import {localStore} from 'libs/storage/storage';
import {sendEventToDataLayerForCustomerData} from 'libs/analytics/analytics.js';

import s from './socialButtons.scss';

export type PropsType = {
    getToken: ActionCreatorType<{accessToken: string; provider: string}>;
    setError: ActionCreatorType<string>;
    socials: StoreInfoType['social'];
    isLogin: boolean;
};

/**
 * Social buttons: google and facebook
 * @param {PropsType} props - component prop
 * @returns {React.ReactElement} component
 */
export const SocialButtons: FC<PropsType> = ({
    getToken,
    setError,
    socials: {facebook, google},
    isLogin,
}) => {
    /**
     * Send analytics before authorization or registration
     * @param {string} socialNetwork - type network
     * @param {string | undefined} error - error message
     */
    const onSendAnalytics = (socialNetwork: string, error?: string) => {
        sendAnalytics(LOGIN.LOGIN_OR_SIGNUP, {
            socialNetwork,
            action: isLogin ? 'Login' : 'SignUp',
            label: error ? `Error - ${error}` : 'Success',
        });
    };

    /**
     * Call when social auth is success
     * @param {GoogleLoginResponse} response - data from social
     */
    const onGoogleSuccess = (response: GoogleLoginResponse | GoogleLoginResponseOffline) => {
        getToken({
            accessToken: (response as GoogleLoginResponse).getAuthResponse().access_token,
            provider: 'google',
        });
        localStore.set(LOGIN_METHOD, 'Google');
        onSendAnalytics('Google');
        if ('getBasicProfile' in response) {
            const email = response.getBasicProfile().getEmail();
            sendHashedEmailEvent(email);
            sendEventToDataLayerForCustomerData(email);
        }
    };

    /**
     * Call when social auth is success
     * @param {ReactFacebookLoginInfo} response - data from social
     */
    const onFacebookSuccess = (response: ReactFacebookLoginInfo) => {
        getToken({
            accessToken: response.accessToken,
            provider: 'facebook',
        });
        localStore.set(LOGIN_METHOD, 'Facebook');
        onSendAnalytics('Facebook');
        if (response.email) {
            sendHashedEmailEvent(response.email);
            sendEventToDataLayerForCustomerData(response.email);
        }
    };

    /**
     * Show error when auth by social is fail.
     * @param {ReactFacebookFailureResponse} response - error from social
     */
    const onFacebookFail = ({status}: ReactFacebookFailureResponse) => {
        if (status && status !== 'unknown') {
            setError(status);
            onSendAnalytics('Facebook', status);
        }
    };

    /**
     * Show error when auth by social is fail.
     * @param {any} error - error from social
     */
    const onGoogleFail = (error: any) => {
        if (propOr('', 'error', error) !== 'popup_closed_by_user') {
            setError(error);
            onSendAnalytics('Google', error);
        }
    };
    return (
        <div className={s.socials}>
            {facebook && (
                <FacebookLogin
                    icon={
                        <div className={s.facebook}>
                            <Icon icon={FacebookIcon} />
                        </div>
                    }
                    appId={facebook}
                    fields="name,email,picture"
                    callback={onFacebookSuccess}
                    onFailure={onFacebookFail}
                    textButton="Facebook"
                    cssClass={s.linkSocial}
                    disableMobileRedirect
                />
            )}
            {google && (
                <GoogleLogin
                    clientId={google}
                    onSuccess={onGoogleSuccess}
                    onFailure={onGoogleFail}
                    render={({onClick}) => (
                        <button type="button" onClick={onClick} className={s.linkSocial}>
                            <div className={s.google} role="presentation">
                                <Icon icon={GoogleIcon} />
                            </div>
                            Google
                        </button>
                    )}
                />
            )}
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
export const mapStateToProps = (store: ApplicationStoreType) => ({
    socials: getStoreInfoState(store).social,
});

export const SocialButtonsConnected = connect(mapStateToProps, {
    setError: setSocialsError,
    getToken: getSocialToken,
})(SocialButtons);
